const $customLiquid = $(".custom-liquid");
const productId = $customLiquid.data("liquid-product-id");
const clientId = $customLiquid.data("liquid-client-id");
$customLiquid.removeAttr("data-liquid-product-id").removeAttr("data-liquid-client-id");

/**
* Initializes the Liquid SDK with the provided client ID.
*
* @returns {Promise<Object>} A promise that resolves to the initialized Liquid SDK instance.
*/
async function initializeLiquid() {
    return await Liquid({ clientId }); // eslint-disable-line no-undef
}

/**
* Fetches products based on the provided shipping address.
*
* @async
* @param {Object} shipAddress - The shipping address object used to fetch products.
* @returns {Promise<Array>} A promise that resolves to an array of products.
*/
async function fetchProducts(shipAddress) {
    const liquid = await initializeLiquid();

    return await liquid.product({
        ids: [productId],
        shipAddress
    });
}

/**
* Fetches address predictions based on the provided search query using the Liquid SDK.
*
* @async
* @param {string} search - The search query for address predictions.
* @returns {Promise<Object>} A promise that resolves to the address predictions.
*/
const getPredictions = async (search) => {
    const liquid = await initializeLiquid();

    return await liquid.address({
        search
    });
};

/**
* Retrieves detailed information about a specific place using its place ID.
*
* @async
* @param {string} placeId - The unique identifier of the place to retrieve details for.
* @returns {Promise<Object>} A promise that resolves to an object containing the place details.
*/
const getPlaceDetails = async (placeId) => {
    const liquid = await initializeLiquid();

    return await liquid.address({
        placeId,
    });
};

/**
* Updates the cart using the Liquid SDK.
*
* This function initializes the Liquid SDK and then updates the cart with the provided cart object.
*
* @param {Object} cartObject - The cart object containing the details to update the cart.
* @returns {Promise<Object>} - A promise that resolves to the updated cart object.
*/
async function updateCart(cartObject) {
    const liquid = await initializeLiquid();

    return await liquid.cart(cartObject);
}

/**
* Initiates the checkout process using the Liquid SDK.
*
* @async
* @param {Object} checkoutObject - The object containing checkout details.
* @returns {Promise<Object>} - A promise that resolves to the result of the checkout process.
*/
async function checkout(checkoutObject) {
    const liquid = await initializeLiquid();

    return await liquid.checkout(checkoutObject);
}


/**
* Resets the personalization form to its initial state.
* This function performs the following actions:
* - Shows the personalization form.
* - Hides the personalization content.
* - Removes the 'has-value' class from all input fields within the form.
* - Shows the personalization back button.
* - Hides the content back button.
* - Clears the preview text.
* - Clears the value of the personalization text input field.
* - Disables the personalization submit button.
*/
function resetPersonalizationForm() {
    $(".js-personalization-form").removeClass("d-none");
    $(".js-personalization-content").addClass("d-none");
    $(".js-personalization-form .input").removeClass("has-value");
    $(".js-personalization-back").removeClass("d-none");
    $(".js-content-back").addClass("d-none");
    $(".js-preview-text").text("");
    $("#personalizationText").val("");
    $("#personalizationSubmit").addClass("disabled");
}


/**
* Sorts the retailers of a given product variant based on engraving availability and price.
* If engraving is active, retailers with engraving options are prioritized.
* Within each group (engraving or non-engraving), retailers are sorted by price in ascending order.
*
* @param {Object} variant - The product variant containing retailer information.
* @returns {Array} - Sorted array of retailers.
*/
function sortRetailersByEngravedAndPrice(variant) {
    if (variant.engravingConfigs && variant.engravingConfigs.active) {
        return variant.retailers.sort((a, b) => {
            if (a.engraving && !b.engraving) {
                return -1;
            }
            if (!a.engraving && b.engraving) {
                return 1;
            }
            return parseFloat(a.price) - parseFloat(b.price);
        });
    } else {
        return variant.retailers.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    }
}

/**
* Formats a given price to a string with two decimal places, using the "en-US" locale.
*
* @param {number} price - The price value to be formatted.
* @returns {string} - The formatted price string with two decimal places.
*/
function formattedPrice(price) {
    return price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
* Calculates the total price of all items in the cart.
*
* This function iterates over each item in the cart, multiplying the price of
* each product by its quantity, and sums up these values to compute the total
* cart value.
*
* @param {Object} cart - The shopping cart object containing cart items.
* @param {Array} cart.cartItems - An array of cart item objects.
* @param {Object} cart.cartItems[].product - The product object within a cart item.
* @param {number} cart.cartItems[].product.price - The price of the product.
* @param {number} cart.cartItems[].quantity - The quantity of the product in the cart.
* @returns {number} The total price of all items in the cart.
*/
function calculateCartTotal(cart) {
    let total = 0;

    cart.cartItems.forEach(item => {
        total += item.product.price * item.quantity;
    });

    return total;
}

module.exports = {
    fetchProducts,
    getPredictions,
    getPlaceDetails,
    updateCart,
    checkout,
    resetPersonalizationForm,
    sortRetailersByEngravedAndPrice,
    formattedPrice,
    calculateCartTotal
};
